/**
 * Ce que la matrice d'habilitations dit des modules, pour l'opérateur connecté.
 *
 * **Ce n'est pas un contrôle d'accès, et cela ne doit jamais le devenir.** `EX-BO-002`
 * est formel : masquer une action dans l'interface ne compte pas. Le serveur refuse, et
 * il refuse exactement pareil, que la barre latérale ait masqué ou non — les contrôles
 * de `RefusDHabilitationSurToutesLesRoutesTest` l'éprouvent en appelant l'API
 * directement, après avoir lu cette route.
 *
 * Ce que cette lecture corrige est l'**autre moitié** du même reproche : proposer dans
 * un menu une destination qui échouera *toujours* n'est pas mieux que de masquer une
 * action qu'on peut faire. Un administrateur technique à qui l'on offre « Identités &
 * KYC » clique, obtient un refus, et apprend à se méfier de son propre menu.
 *
 * **Deux listes, et il en faut deux.** `decidableModules` dit sur quoi la matrice fait
 * autorité — les modules qui portent au moins une action de *consultation*.
 * `readableModules` dit lesquels de ceux-là ce rôle peut ouvrir. Sans la première,
 * l'interface confondrait « interdit » avec « pas encore décrit », et masquerait le
 * tableau de bord, le reporting et le réseau de guichets — trois modules dont l'annexe F
 * ne parle pas encore — à tout le monde, silencieusement.
 */

import type { ApiClient } from './api-client.js';

/** Le chemin, tel que `BackOfficeApi` le sert. */
export const backOfficeModulesPath = '/api/backoffice/session/modules';

/** La réponse du serveur, sans rien y ajouter. */
export interface OperatorModules {
  /** Le code du rôle de l'opérateur connecté. */
  readonly role: string;
  /** Les modules sur lesquels la matrice sait répondre. */
  readonly decidableModules: readonly string[];
  /** Ceux, parmi eux, que ce rôle peut consulter. */
  readonly readableModules: readonly string[];
}

/**
 * Lit les modules de l'opérateur. Aucun droit n'est exigé : c'est une question sur soi.
 *
 * **Ce qui revient du réseau est vérifié avant d'être cru**, comme ce qui sort du
 * stockage l'est dans `session-persistence.ts`. Une réponse à moitié formée — deux
 * champs manquants, un objet à la place d'un tableau — ferait entrer dans le magasin
 * un état que [isModuleUsable] interrogerait ensuite sans rien y trouver, et le menu
 * se viderait ou lèverait. Rendre `null` dit « la matrice n'a rien répondu », ce qui
 * est exactement le cas, et fait rendre tous les modules.
 */
export async function fetchOperatorModules(
  client: ApiClient,
): Promise<OperatorModules | null> {
  const recue = await client.request<unknown>(backOfficeModulesPath);
  return estUneReponseDeModules(recue) ? recue : null;
}

/** La forme attendue, champ par champ. Un `unknown` non vérifié n'entre pas au magasin. */
function estUneReponseDeModules(valeur: unknown): valeur is OperatorModules {
  if (typeof valeur !== 'object' || valeur === null) return false;
  const candidat = valeur as Record<string, unknown>;
  return (
    typeof candidat.role === 'string' &&
    estUnTableauDeChaines(candidat.decidableModules) &&
    estUnTableauDeChaines(candidat.readableModules)
  );
}

function estUnTableauDeChaines(valeur: unknown): valeur is readonly string[] {
  return Array.isArray(valeur) && valeur.every((element) => typeof element === 'string');
}
